import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  type AssistantAction,
  AssistantAction_Kind,
  type AssistantReply,
  AssistantTurn_Role,
} from "@/generated/backend/v1/assistant";
import { errorText } from "@/components/ErrorPanel";
import { useGuides } from "@/guides/GuideProvider";
import { pageAt } from "@/plan-logic/assistant/pages";
import { useClearConversation, useConversation, useSendMessage } from "./api";
import { BlockView } from "./blocks";

const SUGGESTIONS = [
  "How is my plan looking?",
  "What should I do this month?",
  "Can I afford a $40,000 car next spring?",
  "How does a TFSA work?",
];

/** How long a page has to render before its guide can find its anchors. */
const GUIDE_DELAY = 700;

const narrow = () => typeof window !== "undefined" && window.innerWidth < 760;

/**
 * The AbiChat panel: the conversation (the model's words in grey, the engine's results and the
 * FAQ's answers in cards) and the box to write in. Actions the model chose to do at once ("take
 * me to my budget") run when their reply arrives; offered ones are buttons.
 */
export function AssistantPanel({
  onClose,
  ask,
}: {
  onClose: () => void;
  ask?: { q: string; n: number };
}) {
  const conversation = useConversation(true);
  const send = useSendMessage();
  const clear = useClearConversation();
  const location = useLocation();
  const navigate = useNavigate();
  const guides = useGuides();
  const [input, setInput] = useState("");
  const [failed, setFailed] = useState<{ text: string; error: unknown } | null>(null);
  const [pendingText, setPendingText] = useState<string | null>(null);
  const [minimized, setMinimized] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const asked = useRef(0);
  const uuid = conversation.data?.uuid ?? "";

  const act = useCallback(
    (a: AssistantAction) => {
      if (a.kind === AssistantAction_Kind.NAVIGATE || a.kind === AssistantAction_Kind.PAGE_GUIDE) {
        if (!a.href.startsWith("/app")) return;
        void navigate(a.href);
        if (narrow()) setMinimized(true);
        const guide = pageAt(a.href)?.guide;
        if (a.kind === AssistantAction_Kind.PAGE_GUIDE && guide) {
          setTimeout(() => guides?.start(guide, true), GUIDE_DELAY);
        }
      }
    },
    [navigate, guides],
  );

  /** The last action a reply chose to do at once. */
  const runGo = useCallback(
    (reply: AssistantReply) => {
      const go = reply.turns
        .flatMap((t) => t.blocks)
        .flatMap((b) =>
          b.block.oneofKind === "action" && b.block.action.go ? [b.block.action] : [],
        )
        .at(-1);
      if (go) act(go);
    },
    [act],
  );

  const submit = useCallback(
    async (text: string) => {
      const q = text.trim();
      if (!q || send.isPending) return;
      setFailed(null);
      setPendingText(q);
      try {
        const reply = await send.mutateAsync({
          conversationUuid: conversation.data?.uuid ?? "",
          text: q,
          page: location.pathname.replace(/^\/dev/, ""),
        });
        runGo(reply);
      } catch (error) {
        setFailed({ text: q, error });
      } finally {
        setPendingText(null);
      }
    },
    [send, conversation.data?.uuid, location.pathname, runGo],
  );

  // A question from elsewhere (Home's box): asked once the conversation has loaded.
  useEffect(() => {
    if (!ask || ask.n === asked.current || conversation.isPending) return;
    asked.current = ask.n;
    setMinimized(false);
    void submit(ask.q);
  }, [ask, conversation.isPending, submit]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const turns = conversation.data?.turns ?? [];
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns.length, pendingText, failed]);

  if (minimized) {
    return (
      <button
        type="button"
        className="ac-launch"
        onClick={() => setMinimized(false)}
        data-testid="assistant-restore"
      >
        <span className="mk" aria-hidden="true" />
        Back to Abi
      </button>
    );
  }

  const onNavigate = () => narrow() && setMinimized(true);
  return (
    <aside className="ac-panel" aria-label="Ask Abi" data-testid="assistant-panel">
      <header className="ac-head">
        <span className="mk" aria-hidden="true" />
        <div>
          <b>Ask Abi</b>
          <span className="ac-legend">
            <span>
              <i className="ai" aria-hidden="true" />
              AI interpreter, use caution
            </span>
            <span>
              <i className="calc" aria-hidden="true" />
              Cards are calculated from your data
            </span>
          </span>
        </div>
        {uuid && (
          <button
            type="button"
            className="ac-newchat"
            onClick={() => clear.mutate(uuid)}
            disabled={clear.isPending || send.isPending}
            title="Delete this conversation and start a new one"
            data-testid="assistant-clear"
          >
            New chat
          </button>
        )}
        <button type="button" className="cix" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </header>
      <div className="ac-list-wrap" ref={listRef} aria-live="polite">
        {conversation.isError && <p className="ac-error">{errorText(conversation.error)}</p>}
        {turns.length === 0 && !pendingText && !conversation.isPending && (
          <div className="ac-empty-state">
            <p>
              Ask about your plan, try a what-if, or ask how something works. Results from your plan
              come from Abi&rsquo;s planning engine and appear in cards like this:
            </p>
            <div className="ac-sample">Calculated from your data</div>
            <div className="ac-suggest">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="chip" onClick={() => void submit(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {turns.map((t) => {
          const user = t.role === AssistantTurn_Role.USER;
          return (
            <div
              key={t.uuid}
              className={`ac-turn ${user ? "user" : "abi"}`}
              data-testid={`assistant-turn-${user ? "user" : "abi"}`}
            >
              {t.blocks.map((b) => (
                <BlockView
                  key={b.uuid}
                  block={b}
                  user={user}
                  conversation={uuid}
                  onAct={act}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          );
        })}
        {pendingText && (
          <>
            <div className="ac-turn user">
              <div className="ac-user">{pendingText}</div>
            </div>
            <div className="ac-turn abi">
              <div className="ac-typing" role="status" data-testid="assistant-typing">
                <span />
                <span />
                <span />
                <em className="ac-sr">Abi is thinking</em>
              </div>
            </div>
          </>
        )}
        {failed && (
          <div className="ac-error" role="alert" data-testid="assistant-error">
            <span>Abi couldn&rsquo;t answer that. {errorText(failed.error)}</span>{" "}
            <button type="button" className="linkbtn" onClick={() => void submit(failed.text)}>
              Try again
            </button>
          </div>
        )}
      </div>
      <form
        className="ac-input"
        onSubmit={(e) => {
          e.preventDefault();
          const text = input;
          setInput("");
          void submit(text);
        }}
      >
        <textarea
          ref={inputRef}
          value={input}
          rows={2}
          maxLength={4000}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              const text = input;
              setInput("");
              void submit(text);
            }
          }}
          placeholder="Ask Abi about your plan, or try a what-if"
          aria-label="Your message"
          data-testid="assistant-input"
        />
        <button
          type="submit"
          className="btn"
          disabled={send.isPending || !input.trim()}
          data-testid="assistant-send"
        >
          Send
        </button>
      </form>
    </aside>
  );
}
